import { Button, Chip } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useElapsedMs } from '../../../hooks/useElapsedMs';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { SolverEngine } from '../../../types/planung-graphql-client-defs';
import {
  formatClock,
  GAP_INITIALIZING_THRESHOLD,
  gapFormatter,
  solverEngineLabelKey,
} from '../../../utils/timetable/solverDiagnostics';
import { ScoreDebugModal } from '../ScoreDebugModal/ScoreDebugModal';
import { useTimetableStore } from '../TimetableProvider';
import styles from './SolverRunBanner.module.scss';

/**
 * Inline solver status that replaces the card picker inside GridActionArea while a
 * solver job runs. It is the single status surface (the solver wizard no longer hosts
 * a running step), mirroring how CardControl / CardTimefoldAnalyze take over the area.
 */
export const SolverRunBanner: FC = observer(() => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();
  const solver = pinboardStore.solverStore;
  const perms = usePermissionChecker();
  const isAdmin = perms?.isOmniAdmin() ?? false;
  const [scoreDebugOpen, setScoreDebugOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();

  const job = solver.solverJob;
  const elapsedMs = useElapsedMs(job?.createdAt);

  // Guard against the (rare) case where the parent renders us without a job.
  if (!job) {
    return null;
  }

  const phase: 'search' | 'optimize' = job.solutionFound ? 'optimize' : 'search';

  const algoName = t(solverEngineLabelKey(job.engine));

  const elapsedText = elapsedMs == null ? '–' : formatClock(elapsedMs);

  const mathplanGapPercent =
    isAdmin &&
    job.engine === SolverEngine.Mathplan &&
    Number.isFinite(job.gap) &&
    job.gap >= 0 &&
    job.gap < GAP_INITIALIZING_THRESHOLD
      ? gapFormatter.format(job.gap * 100)
      : null;

  const hasPreview = solver.previewPlacements.size > 0;

  const options = solver.solveOptions;
  const optionChips = [
    options.keepExistingPositions && t('pinboard.generate.runBanner.optionChip.keepCards'),
    options.includeRoomResources && t('pinboard.generate.runBanner.optionChip.withRooms'),
    options.includeSubjectContainer && t('pinboard.generate.runBanner.optionChip.subjectContainer'),
  ].filter((label): label is string => Boolean(label));

  return (
    <div className={styles.banner} role='status' aria-live='polite'>
      <div className={styles['icon-wrap']} aria-hidden='true'>
        <div className={styles.ring} />
        {phase === 'optimize' ? <div className={styles.check}>✓</div> : null}
      </div>
      <div className={styles.text}>
        <div className={styles.title}>{t('pinboard.generate.runBanner.running')}</div>
        <div className={styles.meta}>
          {t('pinboard.generate.runBanner.meta', { algo: algoName, elapsed: elapsedText })}
        </div>
        {hasPreview ? (
          <div className={styles.unplaced}>
            {t('pinboard.generate.runBanner.unplaced', { count: solver.previewUnplacedCount })}
          </div>
        ) : null}
        {optionChips.length > 0 ? (
          <div className={styles.chips}>
            {optionChips.map((label) => (
              <Chip key={label} value={label} bgColor='var(--color-primary-light)' color='var(--color-grey-darkest)' />
            ))}
          </div>
        ) : null}
        {mathplanGapPercent != null ? (
          <div className={styles.gap}>
            {t('pinboard.generate.wizard.step3.gapLine', { percent: mathplanGapPercent })}
          </div>
        ) : null}
      </div>
      <div className={styles.actions}>
        {isAdmin ? (
          <Button hierarchy='tertiary' onClick={() => setScoreDebugOpen(true)}>
            {t('pinboard.generate.runBanner.scoreDebug')}
          </Button>
        ) : null}
        <div className={styles['primary-actions']}>
          <Button
            fullWidth
            hierarchy='primary'
            onClick={() => void solver.acceptCurrentSolution()}
            disabled={solver.accepting || discarding || !(job.solutionFound || hasPreview)}
            isLoading={solver.accepting}
          >
            {t('pinboard.generate.wizard.step3.acceptSolution')}
          </Button>
          <Button
            fullWidth
            hierarchy='secondary'
            onClick={async () => {
              // Discarding throws away the running solver's result, so confirm first — this is
              // the escape hatch for a hung job, not a routine action.
              const confirmed = await confirm({
                title: t('pinboard.generate.runBanner.discardConfirm.title'),
                message: t('pinboard.generate.runBanner.discardConfirm.message'),
                confirmText: t('pinboard.generate.runBanner.discardConfirm.confirm'),
                cancelText: t('common.cancel'),
              });
              if (!confirmed) {
                return;
              }
              // abortJob() cancels server-side, clears the snapshot and unmounts this banner,
              // so there is nothing to reset on success.
              setDiscarding(true);
              void solver.abortJob().finally(() => setDiscarding(false));
            }}
            disabled={solver.accepting || discarding}
            isLoading={discarding}
          >
            {t('pinboard.generate.runBanner.discardSolve')}
          </Button>
        </div>
      </div>
      {isAdmin ? <ScoreDebugModal isOpen={scoreDebugOpen} onClose={() => setScoreDebugOpen(false)} /> : null}
      <ConfirmationDialog />
    </div>
  );
});
